import type { Issue } from "contracts/api/issue/responses";
import { ISSUE_SEVERITY } from "contracts/enums/core";
import { CORE_WEB_VITALS_CONFIDENCE_LEVEL } from "contracts/enums/web-vitals";
import type {
  MapActionCategory,
  MapActionEvidenceState,
  MapFutureBriefLevel,
  MapMigrationWaveKey,
} from "../../enums/map";
import {
  MAP_ACTION_CATEGORY,
  MAP_ACTION_EVIDENCE_STATE,
  MAP_CHANGE_TYPE,
  MAP_CONFIDENCE,
  MAP_FUTURE_BRIEF_LEVEL,
  MAP_MIGRATION_WAVE_KEY,
} from "../../enums/map";
import type { MapChange } from "./mapTypes";

export function categoryForMapChange(
  change: MapChange,
): MapActionCategory | null {
  if (change.type === MAP_CHANGE_TYPE.RETAIN_STRUCTURE)
    return MAP_ACTION_CATEGORY.KEEP;
  if (change.type === MAP_CHANGE_TYPE.CONSOLIDATE_PAGES)
    return MAP_ACTION_CATEGORY.MERGE;
  if (change.type === MAP_CHANGE_TYPE.CREATE_HUB)
    return MAP_ACTION_CATEGORY.CREATE;
  if (
    change.type === MAP_CHANGE_TYPE.IMPROVE_PATTERN ||
    change.type === MAP_CHANGE_TYPE.CLARIFY_DESTINATION
  ) {
    return MAP_ACTION_CATEGORY.IMPROVE;
  }
  return null;
}

export function uniqueCanonicalIssues(issues: Issue[]): Issue[] {
  return [
    ...new Map(
      issues.map((issue) => [canonicalIssueKey(issue), issue]),
    ).values(),
  ].sort(
    (left, right) =>
      severityOrder(left.severity) - severityOrder(right.severity) ||
      left.title.localeCompare(right.title),
  );
}

/**
 * A scan-tracked issue is keyed on its stable fingerprint so the same problem
 * stays one entry across re-scans; a hand-created issue has no such identity
 * and is keyed on its row id.
 */
export function canonicalIssueKey(issue: Issue): string {
  return issue.problemKey === null ? `issue:${issue.id}` : issue.fingerprint;
}

export function highestSeverity(issues: Issue[]): Issue["severity"] | null {
  return issues[0]?.severity ?? null;
}

export function evidenceStateForChange(
  change: MapChange,
  issues: Issue[],
): MapActionEvidenceState {
  if (issues.length > 0) return MAP_ACTION_EVIDENCE_STATE.ISSUE_BACKED;
  return change.type === MAP_CHANGE_TYPE.CREATE_HUB
    ? MAP_ACTION_EVIDENCE_STATE.PROVISIONAL
    : MAP_ACTION_EVIDENCE_STATE.STRUCTURAL;
}

export function futureBriefLevelForChange(
  change: MapChange,
): MapFutureBriefLevel {
  if (
    change.type === MAP_CHANGE_TYPE.IMPROVE_PATTERN &&
    change.pages.length === 1
  ) {
    return MAP_FUTURE_BRIEF_LEVEL.PAGE_BRIEF;
  }
  if (
    change.type === MAP_CHANGE_TYPE.IMPROVE_PATTERN ||
    change.type === MAP_CHANGE_TYPE.CLARIFY_DESTINATION
  ) {
    return MAP_FUTURE_BRIEF_LEVEL.FLOW_BRIEF;
  }
  return MAP_FUTURE_BRIEF_LEVEL.ARCHITECTURE_BRIEF;
}

export function migrationWave(
  change: MapChange,
  category: MapActionCategory | null,
  evidenceState: MapActionEvidenceState,
  severity: Issue["severity"] | null,
): MapMigrationWaveKey | null {
  if (category === null) return null;
  if (category === MAP_ACTION_CATEGORY.MERGE)
    return MAP_MIGRATION_WAVE_KEY.SIMPLIFICATION;
  if (category === MAP_ACTION_CATEGORY.CREATE) {
    return evidenceState === MAP_ACTION_EVIDENCE_STATE.ISSUE_BACKED &&
      (severity === ISSUE_SEVERITY.CRITICAL ||
        severity === CORE_WEB_VITALS_CONFIDENCE_LEVEL.HIGH) &&
      change.confidence === MAP_CONFIDENCE.STRONG
      ? MAP_MIGRATION_WAVE_KEY.FOUNDATION
      : MAP_MIGRATION_WAVE_KEY.ADDITIONS;
  }
  if (
    change.type === MAP_CHANGE_TYPE.CLARIFY_DESTINATION ||
    change.type === MAP_CHANGE_TYPE.IMPROVE_PATTERN
  ) {
    return MAP_MIGRATION_WAVE_KEY.FLOW_FIXES;
  }
  return severity === ISSUE_SEVERITY.CRITICAL ||
    severity === CORE_WEB_VITALS_CONFIDENCE_LEVEL.HIGH
    ? MAP_MIGRATION_WAVE_KEY.FOUNDATION
    : MAP_MIGRATION_WAVE_KEY.ADDITIONS;
}

function severityOrder(severity: Issue["severity"]): number {
  return { critical: 0, high: 1, medium: 2, low: 3 }[severity];
}
